import { isNamedPlant } from './operationsQuests';
import { isBillUploadEnabled } from '../constants/uiVisibility';

import {
  IMPLEMENTED_RECOMMENDATIONS_STORAGE_KEY,
  clamp,
  computeEnvironmentalEquivalents as computeEnvironmentalEquivalentsCore,
  computeDataQualityScore as computeDataQualityScoreCore,
  computeCustomerHealthScore as computeCustomerHealthScoreCore,
} from '../shared/sustainabilityUxCore.js';

export type DataConfidenceLevel = 'high' | 'medium' | 'low';

export interface DataQualityInput {
  hasProfile: boolean;
  hasAssessment: boolean;
  carbonEmissionsKg: number;
  transactionCount?: number;
  documentCount?: number;
  hasScopeBreakdown?: boolean;
  /** From inventoryMetadata.completenessScore (0–100) when available. */
  inventoryCompletenessScore?: number;
  /** Share of transactions with activity-based quantification (0–100). */
  activitySharePct?: number;
}

export interface DataQualityResult {
  score: number;
  level: DataConfidenceLevel;
  label: string;
  hints: string[];
}

export interface OnboardingStep {
  id: string;
  label: string;
  description: string;
  path: string;
  complete: boolean;
  /** Rough time estimate shown to MSME users. */
  estimatedMinutes?: number;
}

export interface OnboardingProgress {
  steps: OnboardingStep[];
  completedCount: number;
  totalCount: number;
  percent: number;
  allComplete: boolean;
}

export interface NextBestAction {
  title: string;
  description: string;
  path: string;
  impactLabel?: string;
  effortLabel?: string;
  ctaLabel: string;
}

export interface EnvironmentalEquivalents {
  treesEquivalent: number;
  carsOffRoadDays: number;
  kwhEquivalent: number;
}

export {
  IMPLEMENTED_RECOMMENDATIONS_STORAGE_KEY,
  CARBON_WIZARD_DRAFT_STORAGE_KEY,
} from '../shared/sustainabilityUxCore.js';

export function computeDataQualityScore(input: DataQualityInput): DataQualityResult {
  return computeDataQualityScoreCore({ ...input, platform: 'web' }) as DataQualityResult;
}

export function deriveOnboardingSteps(input: {
  hasProfile?: boolean;
  /** @deprecated Ops mapping is advanced — ignored for the linear MSME checklist. */
  hasWorkflow?: boolean;
  hasDocuments: boolean;
  hasAssessment: boolean;
  /** True after the user picks a report goal on /reporting. */
  hasReportGoal?: boolean;
}): OnboardingProgress {
  const steps: OnboardingStep[] = [];

  if (typeof input.hasProfile === 'boolean') {
    steps.push({
      id: 'profile',
      label: 'Complete company profile',
      description: 'Confirm GST, Udyam, industry, and contact details — required for verification and buyer audits.',
      path: '/msme-profile',
      complete: input.hasProfile,
      estimatedMinutes: 5,
    });
  }

  if (isBillUploadEnabled()) {
    steps.push({
      id: 'documents',
      label: 'Upload your bills',
      description: 'Electricity, fuel, or purchase invoices — we read them automatically.',
      path: '/document-upload',
      complete: input.hasDocuments,
      estimatedMinutes: 3,
    });
  }

  steps.push(
    {
      id: 'assessment',
      label: 'Calculate your footprint',
      description: 'Answer a few questions about your operations to see where emissions come from.',
      path: '/carbon-footprint?section=start-assessment',
      complete: input.hasAssessment,
      estimatedMinutes: 15,
    },
    {
      id: 'report',
      label: 'Pick your report goal',
      description: 'Open Compliance and choose one pack: BRSR, ISO 14064, or CBAM.',
      path: '/reporting',
      complete: Boolean(input.hasReportGoal),
      estimatedMinutes: 5,
    },
  );

  const completedCount = steps.filter((s) => s.complete).length;
  const totalCount = steps.length;

  return {
    steps,
    completedCount,
    totalCount,
    percent: totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0,
    allComplete: completedCount === totalCount,
  };
}

export function deriveNextBestAction(input: {
  hasProfile: boolean;
  hasDocuments: boolean;
  hasAssessment: boolean;
  /** @deprecated Ops mapping is advanced — ignored for linear MSME next actions. */
  hasWorkflow?: boolean;
  hasReportGoal?: boolean;
  carbonScore: number;
  topRecommendationTitle?: string;
  topRecommendationKg?: number;
}): NextBestAction {
  if (!input.hasProfile) {
    return {
      title: 'Start with your company details',
      description: 'Tell us your sector and location — we use this to suggest realistic benchmarks and compliance paths.',
      path: '/msme-profile',
      effortLabel: '~5 min',
      ctaLabel: 'Add company details',
    };
  }

  if (isBillUploadEnabled() && !input.hasDocuments) {
    return {
      title: 'Upload an electricity or fuel bill',
      description: 'A recent bill is the fastest way to improve accuracy. Drag and drop a PDF or photo — no manual typing needed.',
      path: '/document-upload',
      effortLabel: '~3 min',
      impactLabel: 'More accurate footprint',
      ctaLabel: 'Upload a bill',
    };
  }

  if (!input.hasAssessment) {
    return {
      title: 'See your carbon footprint',
      description: 'Walk through a short assessment to find your biggest emission sources and get practical reduction ideas.',
      path: '/carbon-footprint?section=start-assessment',
      effortLabel: '~15 min',
      impactLabel: 'Know your baseline',
      ctaLabel: 'Start assessment',
    };
  }

  if (!input.hasReportGoal) {
    return {
      title: 'Pick one report goal',
      description:
        'Choose buyer, bank, or export — we open the matching disclosure pack with readiness status and downloads.',
      path: '/reporting',
      effortLabel: '~5 min',
      impactLabel: 'Report-ready pack',
      ctaLabel: 'Choose report goal',
    };
  }

  if (input.carbonScore > 0 && input.carbonScore < 60) {
    return {
      title: 'Close gaps for your report',
      description:
        'Follow the 3-step readiness checklist, then download your disclosure pack when scores look healthy.',
      path: '/reporting',
      effortLabel: '~5 min',
      impactLabel: 'Disclosure readiness',
      ctaLabel: 'Open my report',
    };
  }

  if (input.topRecommendationTitle) {
    return {
      title: 'Act on a reduction idea',
      description: `Next: ${input.topRecommendationTitle}${
        input.topRecommendationKg != null && input.topRecommendationKg > 0
          ? ` (about ${Math.round(input.topRecommendationKg).toLocaleString('en-IN')} kg CO₂e).`
          : '.'
      } Then look at government schemes you can apply for yourself.`,
      path: '/carbon-footprint?section=recommendations',
      effortLabel: '~10 min',
      impactLabel: 'Reduce',
      ctaLabel: 'Open reduction ideas',
    };
  }

  return {
    title: 'Reduce, then check government schemes',
    description:
      'Your report pack is ready. Pick a reduction idea, then open Government schemes for public programmes such as ADEETIE and MSME ZED.',
    path: '/carbon-footprint?section=recommendations',
    effortLabel: '~10 min',
    impactLabel: 'Reduce',
    ctaLabel: 'See reduction ideas',
  };
}

export function deriveFinanceNextBestAction(input: {
  hasAssessment: boolean;
  eligibleCount: number;
  inProgressCount: number;
}): NextBestAction | null {
  if (!input.hasAssessment) {
    return {
      title: 'Run a carbon assessment before scheme applications',
      description: 'Government energy and MSME schemes ask for an emissions or energy record. Start with an assessment.',
      path: '/carbon-footprint?section=start-assessment',
      effortLabel: '~15 min',
      impactLabel: 'Scheme evidence',
      ctaLabel: 'Start assessment',
    };
  }

  if (input.eligibleCount > 0) {
    return {
      title: `Review ${input.eligibleCount} government scheme${input.eligibleCount === 1 ? '' : 's'}`,
      description: 'Your reduction record can support applications you file yourself on ADEETIE, ZED, and related portals.',
      path: '/carbon-savings?section=schemes',
      impactLabel: `${input.eligibleCount} to review`,
      effortLabel: '~5 min',
      ctaLabel: 'Open schemes',
    };
  }

  if (input.inProgressCount > 0) {
    return {
      title: 'Finish the evidence schemes usually ask for',
      description: 'Bills, a reduction target, and at least one completed reduction idea make a public application easier to support.',
      path: '/carbon-savings?section=schemes',
      impactLabel: `${input.inProgressCount} in progress`,
      ctaLabel: 'Open schemes',
    };
  }

  return {
    title: 'See which government schemes to open',
    description: 'Set a reduction target, then use the official portals for ADEETIE, MSME ZED, and other public programmes.',
    path: '/carbon-savings?section=schemes',
    effortLabel: '~10 min',
    ctaLabel: 'Open schemes',
  };
}

/** Prefer AI goal-advisor action when MSME advisory orchestration has run. */
export function deriveNextBestActionFromAdvisory(
  fallback: NextBestAction,
  advisoryAction?: {
    title?: string;
    description?: string;
    path?: string;
    priority?: string;
  } | null
): NextBestAction {
  if (!advisoryAction?.path) {
    return fallback;
  }
  return {
    title: advisoryAction.title || fallback.title,
    description: advisoryAction.description || fallback.description,
    path: advisoryAction.path,
    ctaLabel: 'Take action',
    impactLabel: advisoryAction.priority === 'high' ? 'AI priority' : fallback.impactLabel,
    effortLabel: fallback.effortLabel,
  };
}

export interface TrustPanelScores {
  dataCompletenessScore: number;
  inventoryQualityScore: number;
  inventoryQualityLevel?: string;
  uncertaintyNote?: string;
  activitySharePct?: number;
}

/** Rough equivalency factors for user-facing context (not for inventory calculations). */
export interface FrameworkDataGap {
  framework: string;
  gap: string;
  actionPath: string;
  actionLabel: string;
}

/** Map data-quality gaps to disclosure-oriented next steps (BRSR / export). */
export function deriveFrameworkDataGaps(input: DataQualityInput): FrameworkDataGap[] {
  const gaps: FrameworkDataGap[] = [];

  if (!input.hasProfile) {
    gaps.push({
      framework: 'BRSR Core',
      gap: 'Company profile incomplete for sector-specific intensity benchmarks.',
      actionPath: '/msme-profile',
      actionLabel: 'Complete profile',
    });
  }

  if (!input.hasAssessment || input.carbonEmissionsKg <= 0) {
    gaps.push({
      framework: 'BRSR Principle 6',
      gap: 'No baseline greenhouse gas inventory on file.',
      actionPath: '/carbon-footprint?section=start-assessment',
      actionLabel: 'Run assessment',
    });
  }

  if ((input.documentCount ?? 0) === 0) {
    gaps.push({
      framework: 'Assurance readiness',
      gap: 'No document-backed evidence (utility bills, invoices, or connectors).',
      actionPath: isBillUploadEnabled() ? '/document-upload' : '/data?section=connectors',
      actionLabel: isBillUploadEnabled() ? 'Upload evidence' : 'Connect accounting data',
    });
  }

  if (!input.hasScopeBreakdown) {
    gaps.push({
      framework: 'Export (CBAM / CSRD)',
      gap: 'Scope 1–3 split missing for product- and value-chain disclosures.',
      actionPath: '/carbon-footprint?section=assessment-results',
      actionLabel: 'Review scopes',
    });
  }

  if ((input.transactionCount ?? 0) === 0) {
    gaps.push({
      framework: 'Operational inventory',
      gap: 'No classified transactions — spend-based signals improve hotspot accuracy.',
      actionPath: isBillUploadEnabled() ? '/document-upload?tab=1' : '/data?section=connectors',
      actionLabel: isBillUploadEnabled() ? 'Add transactions' : 'Connect accounting data',
    });
  }

  return gaps.slice(0, 4);
}

export function computeEnvironmentalEquivalents(kgCo2: number): EnvironmentalEquivalents {
  return computeEnvironmentalEquivalentsCore(kgCo2);
}

export function loadImplementedRecommendationIds(): Set<string> {
  if (typeof window === 'undefined') {
    return new Set();
  }
  try {
    const raw = window.localStorage.getItem(IMPLEMENTED_RECOMMENDATIONS_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

export function saveImplementedRecommendationIds(ids: Set<string>): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(IMPLEMENTED_RECOMMENDATIONS_STORAGE_KEY, JSON.stringify(Array.from(ids)));
}

export function markRecommendationImplemented(id: string): Set<string> {
  const next = loadImplementedRecommendationIds();
  next.add(id);
  saveImplementedRecommendationIds(next);
  return next;
}

export interface CompletenessResult {
  score: number;
  label: string;
  hints: string[];
}

const hasStr = (v: unknown) => typeof v === 'string' && v.trim().length > 0;

/** MSME company profile completeness for B2B engagement cues. */
export function computeMsmeProfileCompleteness(profile: Record<string, unknown> | null | undefined): CompletenessResult {
  if (!profile) {
    return { score: 0, label: 'Profile not started', hints: ['Register your company to unlock tailored defaults.'] };
  }

  let score = 0;
  const hints: string[] = [];
  const listedCin = (profile.listedCompanyProfile as { cinNumber?: string } | undefined)?.cinNumber;
  const hasUdyamOrCin = hasStr(profile.udyamRegistrationNumber) || hasStr(listedCin) || hasStr(profile.cinNumber);

  const checks: Array<{ ok: boolean; hint: string; weight: number }> = [
    { ok: hasStr(profile.companyName), hint: 'Add legal company name', weight: 12 },
    {
      ok: typeof (profile.manufacturingProfile as { exportActivity?: boolean } | undefined)?.exportActivity === 'boolean',
      hint: 'Choose whether you export outside India',
      weight: 6,
    },
    { ok: hasStr(profile.gstNumber), hint: 'Add GST number for buyer verification', weight: 12 },
    {
      ok: hasUdyamOrCin,
      hint: 'Add Udyam (Udyog) number or CIN',
      weight: 8,
    },
    { ok: hasStr(profile.industry), hint: 'Select industry sector', weight: 8 },
    {
      ok: hasStr((profile.contact as { email?: string })?.email),
      hint: 'Add contact email',
      weight: 8,
    },
    {
      ok: hasStr((profile.contact as { phone?: string })?.phone),
      hint: 'Add contact phone',
      weight: 4,
    },
    {
      ok: (() => {
        const units = (profile.business as {
          manufacturingWorkflow?: { units?: Array<{ name?: string }> };
        })?.manufacturingWorkflow?.units;
        const namedUnit = Array.isArray(units)
          ? units.some((unit) => isNamedPlant(unit?.name))
          : false;
        return namedUnit;
      })(),
      hint: 'Add manufacturing unit name under Profile → Facility & products',
      weight: 11,
    },
    {
      ok: (() => {
        const primaryProducts = (profile.business as { primaryProducts?: string })?.primaryProducts;
        const keyProducts = (profile.manufacturingProfile as { keyProducts?: string[] })?.keyProducts;
        const fromWorkflow = (profile.business as {
          manufacturingWorkflow?: { units?: Array<{ products?: string[] | string }> };
        })?.manufacturingWorkflow?.units?.[0]?.products;
        const workflowProducts = Array.isArray(fromWorkflow)
          ? fromWorkflow.length > 0
          : hasStr(fromWorkflow);
        return hasStr(primaryProducts)
          || (Array.isArray(keyProducts) && keyProducts.length > 0)
          || workflowProducts;
      })(),
      hint: 'Add products manufactured under Profile → Facility & products',
      weight: 11,
    },
    {
      ok: hasStr((profile.manufacturingProfile as { industrySector?: string })?.industrySector),
      hint: 'Complete manufacturing sector details',
      weight: 8,
    },
    {
      ok: (() => {
        const org = (profile.manufacturingProfile as {
          ghgOrganizationalBoundary?: { reportingEntityDescription?: string; consolidationApproach?: string };
        })?.ghgOrganizationalBoundary;
        const orgDesc = typeof org?.reportingEntityDescription === 'string'
          ? org.reportingEntityDescription.trim()
          : '';
        return Boolean(org?.consolidationApproach) && orgDesc.length >= 20;
      })(),
      hint: 'Document organizational boundary under Profile (reporting entity & consolidation)',
      weight: 6,
    },
    {
      ok: (() => {
        const op = (profile.operations as {
          ghgOperationalBoundary?: {
            baseYear?: number;
            scope3CategoriesIncluded?: number[];
            scope2LocationBased?: boolean;
            scope1StationaryCombustion?: boolean;
          };
        })?.ghgOperationalBoundary;
        const scope3Included = op?.scope3CategoriesIncluded;
        const scope3 = Array.isArray(scope3Included) ? scope3Included : [];
        const hasScope1 = Boolean(
          op?.scope1StationaryCombustion
          || (op as { scope1MobileCombustion?: boolean })?.scope1MobileCombustion
        );
        const hasScope2 = Boolean(op?.scope2LocationBased || (op as { scope2MarketBased?: boolean })?.scope2MarketBased);
        return Number(op?.baseYear) >= 1990 && hasScope1 && hasScope2 && scope3.length > 0;
      })(),
      hint: 'Configure operational boundary (Scopes 1–3) under Operations',
      weight: 6,
    },
  ];

  checks.forEach(({ ok, hint, weight }) => {
    if (ok) score += weight;
    else hints.push(hint);
  });

  const finalScore = clamp(score, 0, 100);
  let label = 'Needs attention — complete core fields';
  if (finalScore >= 85) label = 'Audit-ready profile';
  else if (finalScore >= 55) label = 'Good progress — a few gaps remain';

  return { score: finalScore, label, hints: hints.slice(0, 5) };
}

/** Enterprise organization profile completeness. */
export function computeEnterpriseProfileCompleteness(
  profile: Record<string, unknown> | null | undefined
): CompletenessResult {
  if (!profile) {
    return { score: 0, label: 'Organization not registered', hints: ['Complete enterprise registration first.'] };
  }

  let score = 0;
  const hints: string[] = [];
  const facilities = Array.isArray(profile.facilities) ? profile.facilities : [];
  const validFacilities = facilities.filter(
    (f) => hasStr((f as { name?: string }).name) && hasStr((f as { state?: string }).state)
  );
  const scope3 = (profile.scope3Materiality as { categories?: unknown[] })?.categories || [];

  const checks: Array<{ ok: boolean; hint: string; weight: number }> = [
    { ok: hasStr(profile.companyName), hint: 'Add legal company name', weight: 15 },
    { ok: hasStr(profile.cinNumber), hint: 'Add CIN for listing disclosure', weight: 15 },
    { ok: hasStr(profile.industry), hint: 'Add industry sector', weight: 10 },
    { ok: hasStr(profile.consolidationApproach), hint: 'Set consolidation approach', weight: 10 },
    { ok: validFacilities.length >= 1, hint: 'Add at least one facility (Scope 1–2)', weight: 20 },
    {
      ok: scope3.length >= 5,
      hint: 'Configure Scope 3 materiality categories',
      weight: 15,
    },
    {
      ok: Boolean((profile.regulatoryMandates as { sebiBrsr?: boolean })?.sebiBrsr)
        || Boolean((profile.regulatoryMandates as { patScheme?: boolean })?.patScheme),
      hint: 'Confirm applicable regulatory mandates',
      weight: 15,
    },
  ];

  checks.forEach(({ ok, hint, weight }) => {
    if (ok) score += weight;
    else hints.push(hint);
  });

  const finalScore = clamp(score, 0, 100);
  let label = 'Disclosure setup in progress';
  if (finalScore >= 85) label = 'Strong BRSR foundation';
  else if (finalScore >= 55) label = 'Core org data captured';

  return { score: finalScore, label, hints: hints.slice(0, 3) };
}

export function deriveEnterpriseOnboardingSteps(input: {
  hasProfile: boolean;
  profileCompleteness: number;
  workflowComplete: boolean;
  hasDocuments: boolean;
  hasConnectors: boolean;
  hasAssessment: boolean;
}): OnboardingProgress {
  const steps: OnboardingStep[] = [
    {
      id: 'org_profile',
      label: 'Organization profile',
      description: 'CIN, mandates, facilities, and Scope 3 materiality',
      path: '/enterprise/profile',
      complete: input.hasProfile && input.profileCompleteness >= 55,
    },
    {
      id: 'workflow',
      label: 'Compliance workflow',
      description: 'Evidence-backed BRSR / PAT / ICM sections',
      path: '/enterprise/workflow',
      complete: input.workflowComplete,
    },
    {
      id: 'data',
      label: 'Connect data',
      description: 'Accounting connectors or facility documents',
      path: '/enterprise/data?section=connectors',
      complete: input.hasDocuments || input.hasConnectors,
    },
    {
      id: 'inventory',
      label: 'Carbon inventory',
      description: 'Scope 1–3 assessment and disclosure pack',
      path: '/enterprise/carbon-footprint',
      complete: input.hasAssessment,
    },
  ];

  const completedCount = steps.filter((s) => s.complete).length;
  const totalCount = steps.length;

  return {
    steps,
    completedCount,
    totalCount,
    percent: totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0,
    allComplete: completedCount === totalCount,
  };
}

export function deriveEnterpriseNextBestAction(input: {
  profileCompleteness: number;
  workflowComplete: boolean;
  hasDocuments: boolean;
  hasConnectors: boolean;
  hasAssessment: boolean;
  readinessScore: number;
}): NextBestAction {
  if (input.profileCompleteness < 55) {
    return {
      title: 'Strengthen organization profile',
      description: 'Add facilities and Scope 3 materiality so BRSR and PAT workflows have the right boundaries.',
      path: '/enterprise/profile',
      effortLabel: '~10 min',
      ctaLabel: 'Open organization',
    };
  }

  if (!input.workflowComplete) {
    return {
      title: 'Complete compliance workflow sections',
      description: 'Each section unlocks when profile evidence is in place — no empty checkboxes.',
      path: '/enterprise/workflow',
      effortLabel: '~20 min',
      impactLabel: 'Mandate readiness',
      ctaLabel: 'Continue workflow',
    };
  }

  if (!input.hasDocuments && !input.hasConnectors) {
    return {
      title: 'Connect accounting or upload facility bills',
      description: 'Finance-led data improves hotspot accuracy and assurance readiness.',
      path: '/enterprise/data?section=connectors',
      effortLabel: '~5 min',
      impactLabel: 'Higher data quality',
      ctaLabel: 'Add data',
    };
  }

  if (!input.hasAssessment) {
    return {
      title: 'Run enterprise carbon inventory',
      description: 'Build scope 1–3 totals aligned with your selected mandates.',
      path: '/enterprise/carbon-footprint',
      effortLabel: '~15 min',
      impactLabel: 'Baseline inventory',
      ctaLabel: 'Open inventory',
    };
  }

  if (input.readinessScore > 0 && input.readinessScore < 70) {
    return {
      title: 'Close compliance gaps',
      description: 'Review BRSR / PAT gaps before your next reporting cycle.',
      path: '/enterprise/compliance',
      impactLabel: `${input.readinessScore}% readiness`,
      ctaLabel: 'Review compliance',
    };
  }

  return {
    title: 'Generate disclosure report',
    description: 'Export BRSR-aligned outputs when your inventory and workflow are complete.',
    path: '/enterprise/reporting',
    effortLabel: '~5 min',
    ctaLabel: 'Open reporting',
  };
}

/** Combined customer health score for dashboard headers (MSME or enterprise). */
export function computeCustomerHealthScore(parts: {
  profileScore: number;
  dataOrWorkflowScore: number;
  assessmentOrReadinessScore: number;
}): { score: number; label: string } {
  return computeCustomerHealthScoreCore(parts);
}
